import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useResource, formatDate } from "../lib/hooks";
import { uploadFile } from "../lib/files";
import { Modal } from "./Modal";

interface DocumentRequestRow {
    id: string;
    title: string;
    note: string | null;
    due_date: string | null;
    status: string;
}

interface Alerts {
    documentRequests: DocumentRequestRow[];
    unacknowledgedPolicies: Array<{ id: string; name: string; version: string }>;
    openShiftCount: number;
    recentShiftRequests: Array<{ id: string; status: string; decision_note: string | null; starts_at: string }>;
    birthdayWishes: Array<{ message: string; created_at: string; from_name: string }>;
    recentPayslips: Array<{ timesheet_number: string; net_pay: string; paid_at: string }>;
}

/**
 * The banner that greets a signed-in user with the things waiting on them:
 * documents an admin has asked for, policies not yet acknowledged, and open
 * shifts they could pick up. Renders nothing when nothing is waiting — an
 * empty "alerts" panel trains people to ignore the real ones.
 */
export const PortalAlerts = () => {
    const alerts = useResource<Alerts>("/me/alerts");
    const [uploadTarget, setUploadTarget] = useState<DocumentRequestRow | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const data = alerts.data;
    if (!data) return null;

    const wishes = data.birthdayWishes ?? [];
    const payslips = data.recentPayslips ?? [];
    const somethingWaiting =
        payslips.length > 0 ||
        data.documentRequests.length > 0 ||
        data.unacknowledgedPolicies.length > 0 ||
        data.openShiftCount > 0 ||
        wishes.length > 0;

    if (!somethingWaiting) return null;

    const submitDocument = async (request: DocumentRequestRow, file: File) => {
        setBusy(true);
        setError(null);
        try {
            const stored = await uploadFile(file, "document_request");
            await api.post(`/document-requests/${request.id}/submit`, { fileId: stored.id });
            setUploadTarget(null);
            alerts.reload();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Upload failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            {payslips.length > 0 && (
                <div className="alert-banner" style={{ borderColor: "var(--pass)", background: "var(--pass-bg)" }}>
                    <h3>You have been paid</h3>
                    <ul>
                        {payslips.map((payslip) => (
                            <li key={payslip.timesheet_number}>
                                <strong>{payslip.timesheet_number}</strong> — net{" "}
                                {Number(payslip.net_pay).toLocaleString("en-AU", { style: "currency", currency: "AUD" })}{" "}
                                on {formatDate(payslip.paid_at)}. <Link to="/my-pay">View payslip</Link>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {wishes.length > 0 && (
                <div className="alert-banner" style={{ borderColor: "var(--brand-2)", background: "var(--accent-soft)" }}>
                    <h3>Birthday wishes for you</h3>
                    <ul>
                        {wishes.map((wish, index) => (
                            <li key={index}>
                                <strong>{wish.from_name}</strong>: {wish.message}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="alert-banner">
                <h3>Waiting on you</h3>
                <ul>
                    {data.documentRequests.map((request) => (
                        <li key={request.id}>
                            <strong>{request.title}</strong>
                            {request.due_date && <> — due {formatDate(request.due_date)}</>}
                            {request.note && <> · {request.note}</>}{" "}
                            <button className="icon-btn" onClick={() => { setError(null); setUploadTarget(request); }}>
                                Upload document
                            </button>
                        </li>
                    ))}
                    {data.unacknowledgedPolicies.length > 0 && (
                        <li>
                            <strong>{data.unacknowledgedPolicies.length}</strong> polic
                            {data.unacknowledgedPolicies.length === 1 ? "y" : "ies"} to read and acknowledge:{" "}
                            {data.unacknowledgedPolicies.slice(0, 3).map((p) => p.name).join(", ")}
                            {data.unacknowledgedPolicies.length > 3 && "…"}{" "}
                            <Link to="/policies">Go to policies</Link>
                        </li>
                    )}
                    {data.openShiftCount > 0 && (
                        <li>
                            <strong>{data.openShiftCount}</strong> open shift{data.openShiftCount === 1 ? "" : "s"} available
                            to request. <Link to="/rostering">See the roster</Link>
                        </li>
                    )}
                </ul>
            </div>

            {uploadTarget && (
                <Modal
                    title={`Upload — ${uploadTarget.title}`}
                    subtitle="Goes straight to the manager who asked for it."
                    onClose={() => setUploadTarget(null)}
                >
                    {error && <div className="error-banner">{error}</div>}
                    <div className="field">
                        <label htmlFor="doc-file">Document (max 10 MB)</label>
                        <input
                            id="doc-file" type="file"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            disabled={busy}
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) void submitDocument(uploadTarget, file);
                            }}
                        />
                        {busy && <span className="help">Uploading…</span>}
                    </div>
                </Modal>
            )}
        </>
    );
};
